/**
 * Discipline contract types and mapping utilities.
 * Phase 20A: values come from the generated discipline registry (constants/disciplineRegistry.ts),
 * the same data the backend validates against. Do not list disciplines here.
 */

import {
  ActiveDisciplineCode,
  ActiveProviderTypeCode,
  disciplineCodes,
  disciplineForProviderType,
  getDisciplineEntry,
  providerTypeCodes,
} from '../../constants/disciplines';

type DisciplineEnum = { readonly [K in ActiveDisciplineCode]: K };
const disciplineEnum = Object.freeze(Object.fromEntries(disciplineCodes().map(c => [c, c]))) as DisciplineEnum;

/** API discipline values (backend contract): `DisciplineApi.RN`, `Object.values(DisciplineApi)`. */
export const DisciplineApi = disciplineEnum;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type DisciplineApi = ActiveDisciplineCode;

/** UI discipline values; unified with the API (RN is the canonical nursing code). */
export const DisciplineUI = disciplineEnum;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type DisciplineUI = ActiveDisciplineCode;

/**
 * Generic discipline type - union of UI and API disciplines
 */
export type Discipline = DisciplineUI | DisciplineApi | string;

/**
 * Map a UI value or provider type to the API discipline (NURSING and LVN → RN, PTA → PT).
 * Unknown values pass through.
 */
export function mapUiToApiDiscipline(ui: DisciplineUI | string): DisciplineApi | string {
  const mapped = disciplineForProviderType(ui);
  if (mapped && getDisciplineEntry(mapped, { includePlanned: false })) return mapped as DisciplineApi;
  return ui;
}

/**
 * Map an API discipline to the UI value (UI and API are unified; legacy NURSING / LVN → RN).
 */
export function mapApiToUiDiscipline(api: DisciplineApi | string): DisciplineUI | string {
  return mapUiToApiDiscipline(api);
}

/**
 * Map multiple disciplines from UI to API format
 */
export function mapDisciplinesToApi(disciplines: string[]): string[] {
  return disciplines.map(d => mapUiToApiDiscipline(d) as string);
}

/**
 * Map multiple disciplines from API to UI format
 */
export function mapDisciplinesFromApi(disciplines: string[]): string[] {
  return disciplines.map(d => mapApiToUiDiscipline(d) as string);
}

/**
 * Provider types a user can have. LVN is nursing; PTA, COTA and SLPA are assistants of their
 * therapy discipline (Phase 20A). From the registry.
 */
export const PROVIDER_TYPES = providerTypeCodes() as readonly ActiveProviderTypeCode[];

export type ProviderType = ActiveProviderTypeCode;

export function isProviderType(value: string | null | undefined): value is ProviderType {
  return !!value && (PROVIDER_TYPES as readonly string[]).includes(value.toUpperCase());
}

/**
 * Visit discipline for a provider type (LVN → RN, PTA → PT). Unknown values go through the
 * normal discipline mapping.
 */
export function providerTypeToDiscipline(providerType: string): DisciplineApi | string {
  return mapUiToApiDiscipline(providerType);
}

/**
 * True when a provider of this type can be assigned to work of this discipline
 * (LVN for RN, PTA for PT). Mirrors the backend care-team check (EX-08).
 */
export function isProviderTypeCompatible(providerType: string | null | undefined, discipline: string | null | undefined): boolean {
  if (!providerType || !discipline) return false;
  return providerTypeToDiscipline(providerType) === mapUiToApiDiscipline(discipline);
}
